import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../../shared/constants/theme';
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Drawer,
  Alert,
  Snackbar,
  Stack,
  Chip,
  FormLabel,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import {
  deleteChannelMapping,
  deleteChannelMappingsByChannel,
  fetchChannelMappingBoard,
  fetchChannelMappingCatalog,
  fetchChannelMappingRoomPlans,
  applyChannelRoomMappings,
  setChannelMappingsActive,
} from '../../services/channelMappingService';
import {
  getDistributionSectionPaperSx,
  getDistributionSectionHeaderRowSx,
  getDistributionSectionHeaderLeadSx,
  getDistributionSectionIconBoxSx,
  getDistributionSectionIconFontSize,
  getDistributionSectionTitleSx,
  getDistributionSectionSubtitleSx,
  getDistributionActionButtonSx,
  isDistributionMobileLayout,
} from '../../utils/distributionMobileLayout';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getGuestDrawerContentSx,
  getGuestDrawerSelectSx,
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
} from '../../utils/bookingDrawerLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import { sortRatePlans } from '../../utils/ratePlanSort';
import ChannelMappingBoard, { ChannelMappingBoardSkeleton } from './ChannelMappingBoard';

const THEME_COLOR = THEME_COLORS.PRIMARY;

function cloneGroups(groups = []) {
  return groups.map((group) => ({
    ...group,
    rate_plans: (group.rate_plans || []).map((plan) => ({ ...plan })),
  }));
}

function mergeCodeOptions(...lists) {
  const seen = new Set();
  const rows = [];
  lists.flat().forEach((item) => {
    const code = typeof item === 'string' ? item : item?.code;
    if (!code || seen.has(code)) return;
    seen.add(code);
    rows.push({
      code,
      name: typeof item === 'string' ? item : item.name || code,
    });
  });
  return rows;
}

const OCCUPANCY_TOKENS = [
  ['fourteen', 'fo'],
  ['thirteen', 'th'],
  ['twelve', 'tw'],
  ['eleven', 'el'],
  ['hept', 'hp'],
  ['hex', 'h'],
  ['oct', 'o'],
  ['pent', 'p'],
  ['quad', 'q'],
  ['triple', 't'],
  ['single', 's'],
  ['double', 'd'],
  ['non', 'n'],
  ['dec', 'de'],
];

function occupancyToken(planName) {
  const hay = String(planName || '').toLowerCase();
  const match = OCCUPANCY_TOKENS.find(([word]) => hay.includes(word));
  return match ? match[1] : 'd';
}

function mealToken(mealPlan) {
  const meal = String(mealPlan || 'ep').toLowerCase().replace(/[^a-z0-9]/g, '') || 'ep';
  if (meal === 'ro' || meal === 'ep') return 'ep';
  if (meal === 'bb' || meal === 'cp') return 'cp';
  return meal;
}

const OCCUPANCY_CODES = new Set(OCCUPANCY_TOKENS.map(([, token]) => token));
const OCCUPANCY_GUESTS = {
  s: 1, d: 2, t: 3, q: 4, p: 5, h: 6, hp: 7, o: 8,
  n: 9, de: 10, el: 11, tw: 12, th: 13, fo: 14,
};

function hasOccupancyWord(planName) {
  const hay = String(planName || '').toLowerCase();
  return OCCUPANCY_TOKENS.some(([word]) => hay.includes(word));
}

function parseMappingRateCode(code) {
  const parts = String(code || '').toLowerCase().split('-').filter(Boolean);
  if (parts.length < 2) return '';
  const occ = parts[parts.length - 2];
  const meal = mealToken(parts[parts.length - 1]);
  if (!meal || !/^[a-z0-9]{1,8}$/.test(occ)) return '';
  return `${occ}-${meal}`;
}

function mappingRatePlanCode(planName, mealPlan) {
  if (hasOccupancyWord(planName)) {
    return `${occupancyToken(planName)}-${mealToken(mealPlan)}`;
  }
  return '';
}

function ratePlanChannelCode(roomSlug, planName, mealPlan) {
  if (!roomSlug) return '';
  return `${roomSlug}-${mappingRatePlanCode(planName, mealPlan)}`;
}

function roomCapacity(roomType) {
  const cap = Number(roomType?.max_guests || roomType?.capacity || 0);
  return Number.isFinite(cap) && cap > 0 ? cap : 0;
}

function planOccupancyGuests(plan) {
  const parsed = parseMappingRateCode(plan.rate_plan_code || plan.code || '');
  const occ = parsed
    ? parsed.split('-')[0]
    : (hasOccupancyWord(plan.rate_plan_name || plan.name) ? occupancyToken(plan.rate_plan_name || plan.name) : '');
  return OCCUPANCY_GUESTS[occ] || null;
}

function planFitsRoomCapacity(plan, roomType) {
  const guests = planOccupancyGuests(plan);
  const cap = roomCapacity(roomType);
  if (guests == null || !cap) return true;
  return guests <= cap;
}

function isPmsRatePlan(plan) {
  return plan?.is_pms_enabled === true || plan?.enablePMS === true;
}

function isChannelManagerEnabledPlan(plan) {
  if (plan && ('is_channel_manager_enabled' in plan || 'enableChannelManager' in plan)) {
    return plan.is_channel_manager_enabled === true || plan.enableChannelManager === true;
  }
  return !isPmsRatePlan(plan);
}

function isMappingRatePlan(plan) {
  if (!isChannelManagerEnabledPlan(plan)) return false;
  const code = plan.rate_plan_code || plan.code || '';
  if (parseMappingRateCode(code)) return true;
  if (hasOccupancyWord(plan.rate_plan_name || plan.name)) return true;
  return Boolean(plan.mapping_id);
}

function normalizeCatalogPlan(plan) {
  const name = plan.rate_plan_name || plan.name || '';
  const mealPlan = plan.meal_plan;
  const rawCode = String(plan.rate_plan_code || plan.code || plan.channel_rate_code || '').trim();
  return {
    rate_plan_id: plan.rate_plan_id || plan.id || null,
    rate_plan_name: name,
    rate_plan_code: rawCode,
    meal_plan: mealPlan,
    sort_order: isPmsRatePlan(plan) ? 0 : (plan.sort_order ?? plan.sortOrder ?? 0),
    channel_rate_code: String(plan.channel_rate_code || rawCode || '').trim(),
    mapping_id: plan.mapping_id || null,
    is_active: plan.is_active !== false,
    selected: Boolean(plan.selected),
  };
}

function planIdentity(plan) {
  return String(
    plan.channel_rate_code
    || plan.rate_plan_code
    || plan.code
    || plan.rate_plan_id
    || plan.id
    || ''
  ).trim();
}

function plansFromAiosellRoom(room) {
  return (room?.rateplans || []).map((plan) => {
    const code = String(plan.code || plan.rateplan_id || plan.rateplanCode || '').trim();
    return {
      rate_plan_id: null,
      rate_plan_name: plan.name || plan.rateplan_name || plan.rateplanName || code,
      rate_plan_code: code,
      meal_plan: plan.meal_plan,
      sort_order: 0,
      channel_rate_code: code,
      mapping_id: null,
      is_active: true,
      selected: Boolean(code),
    };
  }).filter((plan) => plan.rate_plan_code);
}

function selectedPlans(group) {
  return (group.rate_plans || []).filter((plan) => plan.selected);
}

function unwrapCatalogPayload(data) {
  if (data?.groups || data?.room_types || data?.rate_plans) return data;
  if (data?.data?.groups || data?.data?.room_types || data?.data?.rate_plans) return data.data;
  return data || {};
}

function normalizeApiGroup(group) {
  const hasMapped = (group.rate_plans || []).some((plan) => plan.mapping_id);
  const hasRoomCode = Boolean(group.channel_room_code);
  const seen = new Set();
  return {
    ...group,
    rate_plans: sortRatePlans(
      (group.rate_plans || [])
        .map((plan) => {
          const row = normalizeCatalogPlan(plan);
          const code = row.channel_rate_code || row.rate_plan_code;
          if (!code || seen.has(code)) return null;
          seen.add(code);
          return {
            ...row,
            rate_plan_code: code,
            channel_rate_code: code,
            mapping_id: plan.mapping_id || row.mapping_id || null,
            selected: hasMapped
              ? Boolean(plan.mapping_id || plan.selected)
              : hasRoomCode,
          };
        })
        .filter(Boolean)
    ),
  };
}


const ChannelMappingSection = () => {
  const theme = useTheme();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const isMobileLayout = isDistributionMobileLayout();
  const drawerTheme = getBookingDrawerTheme(isDark);
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? drawerTheme.surface : '#ffffff'),
    [isDark, drawerTheme.surface]
  );

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const [mappings, setMappings] = useState([]);
  const [channels, setChannels] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [roomPlansById, setRoomPlansById] = useState({});
  const [loadingRoomIds, setLoadingRoomIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const roomPlansCacheRef = useRef({});
  const roomPlansInFlightRef = useRef(new Map());
  const boardLoadIdRef = useRef(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [channelDeleteDialogOpen, setChannelDeleteDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [channelToDelete, setChannelToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingChannelId, setDeletingChannelId] = useState(null);
  const [pendingMappingIds, setPendingMappingIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedChannelIds, setSelectedChannelIds] = useState([]);
  const [forceCreateRatePlans, setForceCreateRatePlans] = useState(false);
  const [errors, setErrors] = useState({});
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [roomGroups, setRoomGroups] = useState([]);
  const catalogCacheRef = React.useRef({});

  const channelRooms = catalog?.channel_rooms || [];
  const visibleGroups = useMemo(() => {
    if (!editingMapping?.room_type_id) return roomGroups;
    const focused = roomGroups.filter((group) => String(group.room_type_id) === String(editingMapping.room_type_id));
    const rest = roomGroups.filter((group) => String(group.room_type_id) !== String(editingMapping.room_type_id));
    return [...focused, ...rest];
  }, [editingMapping, roomGroups]);

  const selectedChannelIdSet = useMemo(
    () => new Set(selectedChannelIds.map((id) => String(id))),
    [selectedChannelIds]
  );
  const allChannelsSelected = channels.length > 0 && selectedChannelIds.length === channels.length;
  const autoMappingCount = useMemo(
    () =>
      roomGroups
        .filter((group) => group.channel_room_code)
        .reduce((sum, group) => sum + selectedPlans(group).length, 0),
    [roomGroups]
  );
  const alreadyMappedSelectedNames = useMemo(() => {
    const mappedChannelIds = new Set(mappings.map((row) => String(row.channel_id)));
    return channels
      .filter((channel) => selectedChannelIdSet.has(String(channel.id)) && mappedChannelIds.has(String(channel.id)))
      .map((channel) => channel.name);
  }, [channels, mappings, selectedChannelIdSet]);

  const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data || {};
    if (data.error) return data.error;
    if (data.message) return data.message;
    const payload = data.errors || {};
    const first = payload.__all__ || payload.non_field_errors || payload.rooms || payload.rate_plans || payload.channel_ids || payload.detail;
    if (Array.isArray(first) && first.length) return first.join(', ');
    if (typeof first === 'string' && first.trim()) return first;
    return fallback;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const loadId = ++boardLoadIdRef.current;
    const showSkeleton = !roomTypes.length && !channels.length;
    if (showSkeleton) setLoading(true);
    try {
      const board = await fetchChannelMappingBoard();
      if (loadId !== boardLoadIdRef.current) return;
      roomPlansCacheRef.current = {};
      roomPlansInFlightRef.current.clear();
      setRoomPlansById({});
      setLoadingRoomIds([]);
      setMappings(board.mappings || []);
      setChannels(board.channels || []);
      setRoomTypes(board.room_types || []);
      setRatePlans([]);
    } catch (error) {
      if (loadId !== boardLoadIdRef.current) return;
      setSnackbar({
        open: true,
        message: 'Failed to load data. Please try again.',
        severity: 'error',
      });
    } finally {
      if (loadId === boardLoadIdRef.current) setLoading(false);
    }
  };

  const handleExpandRoom = useCallback(async (roomTypeId) => {
    const key = String(roomTypeId);
    if (!key) return;
    const cached = roomPlansCacheRef.current[key];
    if (Array.isArray(cached)) {
      setRoomPlansById((current) => (
        current[key] === cached ? current : { ...current, [key]: cached }
      ));
      return;
    }
    if (roomPlansInFlightRef.current.has(key)) return;
    const loadId = boardLoadIdRef.current;
    roomPlansInFlightRef.current.set(key, loadId);
    setLoadingRoomIds((current) => (current.includes(key) ? current : [...current, key]));
    try {
      const data = await fetchChannelMappingRoomPlans(roomTypeId);
      if (loadId !== boardLoadIdRef.current) return;
      const plans = data?.rate_plans || [];
      roomPlansCacheRef.current[key] = plans;
      setRoomPlansById((current) => ({ ...current, [key]: plans }));
    } catch (error) {
      if (loadId !== boardLoadIdRef.current) return;
      roomPlansCacheRef.current[key] = [];
      setRoomPlansById((current) => ({ ...current, [key]: [] }));
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, 'Failed to load rate plans for this room.'),
        severity: 'error',
      });
    } finally {
      if (roomPlansInFlightRef.current.get(key) === loadId) {
        roomPlansInFlightRef.current.delete(key);
      }
      if (loadId === boardLoadIdRef.current) {
        setLoadingRoomIds((current) => current.filter((id) => id !== key));
      }
    }
  }, []);

  const applyCatalog = useCallback((data) => {
    const payload = unwrapCatalogPayload(data);
    const catalogGroups = payload.groups || [];
    const roomTypeRows = payload.room_types || roomTypes;
    const sourceGroups = catalogGroups.length
      ? catalogGroups
      : roomTypeRows.map((roomType) => ({
          room_type_id: roomType.id,
          room_type_name: roomType.name,
          room_type_code: roomType.channel_room_code || roomType.code,
          capacity: roomType.capacity,
          max_guests: roomType.max_guests,
          channel_room_code: '',
          rate_plans: [],
        }));
    setRoomGroups(cloneGroups(sourceGroups.map((group) => {
      const roomType = roomTypeRows.find(
        (item) => String(item.id || item.room_type_id) === String(group.room_type_id)
      ) || {};
      return normalizeApiGroup({
        ...group,
        capacity: group.capacity ?? roomType.capacity,
        max_guests: group.max_guests ?? roomType.max_guests,
      });
    })));
  }, [roomTypes]);

  const loadCatalog = useCallback(async (channelId) => {
    if (!channelId) {
      setCatalog(null);
      setRoomGroups([]);
      return;
    }
    const cacheKey = String(channelId);
    if (catalogCacheRef.current[cacheKey]) {
      const cached = catalogCacheRef.current[cacheKey];
      setCatalog(cached);
      applyCatalog(cached);
      return;
    }
    setCatalogLoading(true);
    try {
      const data = unwrapCatalogPayload(await fetchChannelMappingCatalog(channelId));
      catalogCacheRef.current[cacheKey] = data;
      setCatalog(data);
      applyCatalog(data);
    } catch (error) {
      const fallback = {
        source: 'aiosell',
        room_types: roomTypes,
        rate_plans: [],
        channel_rooms: [],
        groups: [],
        fetched: false,
        error: getApiErrorMessage(error, 'Could not load Aiosell property details.'),
      };
      setCatalog(fallback);
      applyCatalog(fallback);
    } finally {
      setCatalogLoading(false);
    }
  }, [applyCatalog, roomTypes]);

  const handleOpenDrawer = (mapping = null, options = {}) => {
    const channelId = mapping?.channel_id || options.channelId || '';
    setEditingMapping(mapping);
    setSelectedChannelIds(channelId ? [channelId] : []);
    setForceCreateRatePlans(false);
    setErrors({});
    setRoomGroups([]);
    setCatalog(null);
    catalogCacheRef.current = {};
    setDrawerOpen(true);
  };

  const primaryChannelId = selectedChannelIds[0] || '';

  useEffect(() => {
    if (!drawerOpen) return undefined;
    if (!primaryChannelId) {
      setCatalog(null);
      setRoomGroups([]);
      return undefined;
    }
    loadCatalog(primaryChannelId);
    return undefined;
  }, [drawerOpen, primaryChannelId, loadCatalog]);

  const handleOpenChannelColumn = (channel, mapping = null) => {
    handleOpenDrawer(mapping, { channelId: channel?.id });
  };

  const handleCloseDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    setEditingMapping(null);
    setSelectedChannelIds([]);
    setForceCreateRatePlans(false);
    setRoomGroups([]);
    setErrors({});
  };

  const handleToggleChannel = (channelId) => {
    setSelectedChannelIds((prev) => {
      const exists = prev.some((id) => String(id) === String(channelId));
      const next = exists
        ? prev.filter((id) => String(id) !== String(channelId))
        : [...prev, channelId];
      if (!next.length) {
        setCatalog(null);
        setRoomGroups([]);
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next.channel_id;
      delete next.groups;
      return next;
    });
  };

  const handleToggleAllChannels = () => {
    const allIds = channels.map((channel) => channel.id);
    const allSelected = allIds.length > 0 && selectedChannelIds.length === allIds.length;
    const next = allSelected ? [] : allIds;
    setSelectedChannelIds(next);
    if (!next.length) {
      setCatalog(null);
      setRoomGroups([]);
    }
    setErrors((prev) => {
      const cleared = { ...prev };
      delete cleared.channel_id;
      delete cleared.groups;
      return cleared;
    });
  };

  const handleGroupRoomCodeChange = (roomTypeId, roomCode) => {
    const aiosellRoom = channelRooms.find((room) => room.code === roomCode);
    setRoomGroups((prev) =>
      prev.map((group) => {
        if (String(group.room_type_id) !== String(roomTypeId)) return group;
        const nextPlans = aiosellRoom
          ? plansFromAiosellRoom(aiosellRoom)
          : [];
        return normalizeApiGroup({
          ...group,
          channel_room_code: roomCode,
          rate_plans: nextPlans,
        });
      })
    );
  };

  const handleGroupRatePlansChange = (roomTypeId, ratePlanIds) => {
    const wanted = new Set((ratePlanIds || []).map(String));
    setRoomGroups((prev) =>
      prev.map((group) => {
        if (String(group.room_type_id) !== String(roomTypeId)) return group;
        return {
          ...group,
          rate_plans: (group.rate_plans || []).map((plan) => ({
            ...plan,
            selected: wanted.has(planIdentity(plan)) || wanted.has(String(plan.rate_plan_id)),
          })),
        };
      })
    );
  };

  const handleSelectRoomPlans = (roomTypeId, mode) => {
    setRoomGroups((prev) =>
      prev.map((group) => {
        if (String(group.room_type_id) !== String(roomTypeId)) return group;
        return {
          ...group,
          rate_plans: (group.rate_plans || []).map((plan) => ({
            ...plan,
            selected: mode === 'all',
          })),
        };
      })
    );
  };

  const handleSave = async () => {
    const nextErrors = {};
    if (!selectedChannelIds.length) {
      nextErrors.channel_id = 'Select at least one channel';
    }
    const rooms = roomGroups
      .filter((group) => group.channel_room_code)
      .map((group) => ({
        room_type_id: group.room_type_id,
        channel_room_code: group.channel_room_code,
        rate_plan_ids: selectedPlans(group).map((plan) => plan.rate_plan_id).filter(Boolean),
        occupancy_keys: selectedPlans(group).map((plan) => plan.channel_rate_code || plan.rate_plan_code).filter(Boolean),
      }));
    if (selectedChannelIds.length && rooms.length === 0) {
      nextErrors.groups = 'Map at least one room and select its rate plans.';
    } else if (rooms.some((room) => !room.occupancy_keys.length && !room.rate_plan_ids.length)) {
      nextErrors.groups = 'Select at least one rate plan for each mapped room.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    try {
      const result = await applyChannelRoomMappings({
        channel_ids: selectedChannelIds,
        rooms,
        is_active: true,
        force_create_rate_plans: forceCreateRatePlans,
      });
      catalogCacheRef.current = {};
      setSnackbar({
        open: true,
        message: result?.message || `Created mappings for ${rooms.length} room(s) across ${selectedChannelIds.length} channel(s).`,
        severity: 'success',
      });
      setDrawerOpen(false);
      setEditingMapping(null);
      setSelectedChannelIds([]);
      setForceCreateRatePlans(false);
      fetchData();
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        const payload = error.response.data.errors;
        const allErrors = payload.__all__ || payload.rooms || payload.channel_ids || payload.detail;
        const message = Array.isArray(allErrors)
          ? allErrors.join(', ')
          : typeof allErrors === 'string'
            ? allErrors
            : getApiErrorMessage(error, 'Failed to save channel mapping. Please try again.');
        setSnackbar({ open: true, message, severity: 'error' });
      } else {
        setSnackbar({
          open: true,
          message: getApiErrorMessage(error, 'Failed to save channel mapping. Please try again.'),
          severity: 'error',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMappings = async (rows, nextActive) => {
    const ids = (rows || []).map((row) => row.id).filter((id) => id != null);
    if (!ids.length) return;
    const idSet = new Set(ids.map(String));
    const previous = new Map(
      (rows || [])
        .filter((row) => row.id != null)
        .map((row) => [String(row.id), row.is_active !== false])
    );
    setPendingMappingIds((current) => Array.from(new Set([...current, ...ids])));
    setMappings((current) =>
      current.map((row) => (idSet.has(String(row.id)) ? { ...row, is_active: nextActive } : row))
    );
    try {
      const result = await setChannelMappingsActive(ids, nextActive);
      setSnackbar({
        open: true,
        message:
          result?.message
          || (nextActive
            ? `Enabled ${ids.length} mapping${ids.length === 1 ? '' : 's'}`
            : `Disabled ${ids.length} mapping${ids.length === 1 ? '' : 's'}`),
        severity: 'success',
      });
    } catch (error) {
      setMappings((current) =>
        current.map((row) =>
          previous.has(String(row.id))
            ? { ...row, is_active: previous.get(String(row.id)) }
            : row
        )
      );
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, 'Failed to update mapping status. Please try again.'),
        severity: 'error',
      });
    } finally {
      setPendingMappingIds((current) => current.filter((id) => !idSet.has(String(id))));
    }
  };

  const handleDeleteClick = (mapping) => {
    setMappingToDelete(mapping);
    setDeleteDialogOpen(true);
  };

  const handleDeleteChannelClick = (group) => {
    setChannelToDelete(group);
    setChannelDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!mappingToDelete) return;

    setDeletingId(mappingToDelete.id);
    try {
      await deleteChannelMapping(mappingToDelete.id);
      catalogCacheRef.current = {};
      setSnackbar({
        open: true,
        message: 'Channel mapping deleted permanently',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setMappingToDelete(null);
      fetchData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, 'Failed to delete channel mapping. Please try again.'),
        severity: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteChannelConfirm = async () => {
    const channelId = channelToDelete?.channel?.id;
    if (!channelId) return;

    setDeletingChannelId(channelId);
    try {
      const result = await deleteChannelMappingsByChannel(channelId);
      catalogCacheRef.current = {};
      setSnackbar({
        open: true,
        message: result?.message || `Deleted all mappings for ${channelToDelete.channel.name}`,
        severity: 'success',
      });
      setChannelDeleteDialogOpen(false);
      setChannelToDelete(null);
      fetchData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, 'Failed to delete channel mappings. Please try again.'),
        severity: 'error',
      });
    } finally {
      setDeletingChannelId(null);
    }
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={getDistributionSectionPaperSx(theme, isMobileLayout)}
      >
        <Box sx={getDistributionSectionHeaderRowSx(isMobileLayout)}>
          <Box sx={getDistributionSectionHeaderLeadSx(isMobileLayout)}>
            <Box sx={getDistributionSectionIconBoxSx(THEME_COLOR, isMobileLayout)}>
              <LinkIcon sx={{ fontSize: getDistributionSectionIconFontSize(isMobileLayout), color: THEME_COLOR }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={getDistributionSectionTitleSx(isMobileLayout)}>
                Channel Mapping
              </Typography>
              <Typography variant="body2" sx={getDistributionSectionSubtitleSx(isMobileLayout)}>
                Map rooms once. Channel Manager rate plans are created from those rooms.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDrawer()}
            sx={getDistributionActionButtonSx(THEME_COLOR, isMobileLayout)}
          >
            Add Mapping
          </Button>
        </Box>

      {loading ? (
        <ChannelMappingBoardSkeleton isDark={isDark} />
      ) : !roomTypes.length || !channels.length ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            backgroundColor: 'action.hover',
            borderRadius: 2,
          }}
        >
          <LinkIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
            {!channels.length ? 'No channels found' : 'No room types found'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Add channels and room types first, then map them here.
          </Typography>
        </Box>
      ) : (
        <ChannelMappingBoard
          isDark={isDark}
          roomTypes={roomTypes}
          roomPlansById={roomPlansById}
          loadingRoomIds={loadingRoomIds}
          channels={channels}
          mappings={mappings}
          pendingMappingIds={pendingMappingIds}
          onExpandRoom={handleExpandRoom}
          onOpenChannel={handleOpenChannelColumn}
          onToggleMappings={handleToggleMappings}
          onDeleteChannel={handleDeleteChannelClick}
        />
      )}
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, {
            width: BOOKING_DRAWER_WIDTH_SX,
            height: '100%',
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            flexShrink: 0,
            ...getBookingDrawerHeaderSx(isDark),
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <LinkIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 24, flexShrink: 0 }} />
            <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600, fontSize: '1.05rem' }}>
              {editingMapping ? 'Edit Channel Mapping' : 'Add Channel Mapping'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
            onClick={handleCloseDrawer}
            disabled={saving}
            sx={{
              borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
              color: BOOKING_DRAWER_ACCENT,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '999px',
              px: 1.5,
              py: 0.3,
              minWidth: 84,
              flexShrink: 0,
              '&:hover': {
                borderColor: BOOKING_DRAWER_ACCENT,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
              },
              '&.Mui-disabled': {
                borderColor: isDark ? drawerTheme.surfaceBorder : '#d0d0d0',
                color: isDark ? drawerTheme.subtleText : '#999',
              },
            }}
          >
            Close
          </Button>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...getGuestDrawerContentSx(isDark),
            '& .MuiInputBase-input, & .MuiSelect-select': {
              fontSize: '14px',
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.8rem',
            },
          }}
        >
          <Box sx={{ flexShrink: 0, px: 1.5, pt: 1.5, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
              <FormLabel
                required
                sx={{
                  color: errors.channel_id ? '#f44336' : (isDark ? drawerTheme.text : '#333'),
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  '&.Mui-focused': { color: errors.channel_id ? '#f44336' : BOOKING_DRAWER_ACCENT },
                }}
              >
                Channel
              </FormLabel>
              {channels.length > 0 && (
                <Chip
                  size="small"
                  clickable
                  icon={
                    allChannelsSelected
                      ? <CheckBoxIcon sx={{ fontSize: '15px !important' }} />
                      : <CheckBoxOutlineBlankIcon sx={{ fontSize: '15px !important' }} />
                  }
                  label="Select all"
                  onClick={handleToggleAllChannels}
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    bgcolor: allChannelsSelected ? alpha(THEME_COLOR, isDark ? 0.22 : 0.18) : 'transparent',
                    color: isDark ? '#e2e8f0' : '#334155',
                    border: `1px solid ${alpha(THEME_COLOR, 0.35)}`,
                    '& .MuiChip-icon': { color: THEME_COLOR },
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {channels.map((channel) => {
                const selected = selectedChannelIdSet.has(String(channel.id));
                return (
                  <Chip
                    key={channel.id}
                    clickable
                    size="small"
                    icon={
                      selected
                        ? <CheckBoxIcon sx={{ fontSize: '16px !important' }} />
                        : <CheckBoxOutlineBlankIcon sx={{ fontSize: '16px !important' }} />
                    }
                    label={channel.name}
                    onClick={() => handleToggleChannel(channel.id)}
                    sx={{
                      height: 28,
                      borderRadius: '999px',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      px: 0.25,
                      bgcolor: selected
                        ? alpha(THEME_COLOR, isDark ? 0.22 : 0.18)
                        : isDark ? alpha('#fff', 0.04) : '#fff',
                      color: selected ? (isDark ? THEME_COLOR : '#047857') : (isDark ? '#cbd5e1' : '#475569'),
                      border: `1px solid ${selected ? THEME_COLOR : isDark ? alpha('#fff', 0.12) : '#dbe3ee'}`,
                      '& .MuiChip-icon': { color: selected ? THEME_COLOR : (isDark ? '#94a3b8' : '#94a3b8') },
                      '&:hover': {
                        bgcolor: alpha(THEME_COLOR, isDark ? 0.16 : 0.12),
                        borderColor: THEME_COLOR,
                      },
                    }}
                  />
                );
              })}
            </Box>
            {errors.channel_id ? (
              <FormHelperText error sx={{ ml: 0, mt: 0.5 }}>{errors.channel_id}</FormHelperText>
            ) : alreadyMappedSelectedNames.length ? (
              <Typography sx={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', mt: 0.75 }}>
                Selected channel code mapping is already available for {alreadyMappedSelectedNames.join(', ')}.
              </Typography>
            ) : selectedChannelIds.length > 1 ? (
              <Typography sx={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', mt: 0.75 }}>
                The same room and rate codes will be applied to all selected channels.
              </Typography>
            ) : selectedChannelIds.length === 0 ? (
              <Typography sx={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', mt: 0.75 }}>
                Select one or more channels to load room types and rate plans.
              </Typography>
            ) : null}
            {selectedChannelIds.length > 0 && (
              <FormControlLabel
                sx={{ mt: 0.75, ml: 0, alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    size="small"
                    checked={forceCreateRatePlans}
                    onChange={(event) => setForceCreateRatePlans(event.target.checked)}
                    sx={{
                      pt: 0.15,
                      color: THEME_COLOR,
                      '&.Mui-checked': { color: THEME_COLOR },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: drawerTheme.text, lineHeight: 1.3 }}>
                      Force create rate plan
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', lineHeight: 1.35 }}>
                      {forceCreateRatePlans
                        ? 'Create missing Channel Manager occupancy plans for the rate codes you selected, then map them. Existing plans are reused.'
                        : 'Map the selected rate codes to existing Channel Manager plans only. No new rate plan is created unless you turn this on.'}
                    </Typography>
                  </Box>
                }
              />
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: 1.5,
              pb: 1.5,
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              scrollbarWidth: 'thin',
              scrollbarColor: `${alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.45 : 0.35)} transparent`,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.4 : 0.3),
                borderRadius: 3,
              },
            }}
          >
          {selectedChannelIds.length > 0 && catalogLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: drawerTheme.subtleText }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading room types…</Typography>
            </Stack>
          )}

          {errors.groups && <FormHelperText error>{errors.groups}</FormHelperText>}

          {selectedChannelIds.length > 0 && !catalogLoading && (
            <>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: drawerTheme.text, mb: 0.5 }}>
                Map rooms
              </Typography>
              <Typography sx={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', mb: 1 }}>
                Rate plan codes come from the Aiosell property API only. Nothing extra is added locally.
              </Typography>
              {catalog?.error ? (
                <FormHelperText error sx={{ mx: 0, mb: 1 }}>{catalog.error}</FormHelperText>
              ) : null}
              {visibleGroups.map((group) => {
                const isFocused = editingMapping && String(group.room_type_id) === String(editingMapping.room_type_id);
                const roomOptions = mergeCodeOptions(channelRooms, group.channel_room_code ? [{ code: group.channel_room_code, name: group.room_type_name }] : []);
                const chosenPlans = selectedPlans(group);
                const selectedPlanIds = chosenPlans.map((plan) => planIdentity(plan));
                return (
                  <Box
                    key={group.room_type_id}
                    sx={{
                      border: `1px solid ${isFocused ? THEME_COLOR : (isDark ? drawerTheme.surfaceBorder : '#e8e8e8')}`,
                      borderRadius: 1.25,
                      mb: 1,
                      backgroundColor: isDark ? alpha('#fff', 0.02) : '#fff',
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        backgroundColor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.1 : 0.07),
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: drawerTheme.text, fontSize: '0.85rem', lineHeight: 1.3 }}>
                          {group.room_type_name}
                        </Typography>
                        <Typography sx={{ color: drawerTheme.subtleText, fontSize: '0.7rem' }}>
                          {chosenPlans.length} of {(group.rate_plans || []).length} rate plan{chosenPlans.length === 1 ? '' : 's'} selected
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        clickable
                        label="All"
                        onClick={() => handleSelectRoomPlans(group.room_type_id, 'all')}
                        sx={{
                          height: 22,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: isDark ? alpha('#fff', 0.06) : '#fff',
                          border: `1px solid ${isDark ? alpha('#fff', 0.12) : '#dbe3ee'}`,
                        }}
                      />
                    </Box>
                    <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Channel room code</InputLabel>
                        <Select
                          value={group.channel_room_code || ''}
                          label="Channel room code"
                          onChange={(event) => handleGroupRoomCodeChange(group.room_type_id, event.target.value)}
                          renderValue={(value) => value}
                          sx={getGuestDrawerSelectSx(isDark)}
                          MenuProps={drawerOverlays.selectMenuProps}
                        >
                          <MenuItem value="">Don’t map this room</MenuItem>
                          {roomOptions.map((option) => (
                            <MenuItem key={option.code} value={option.code} sx={{ whiteSpace: 'normal' }}>
                              {option.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth size="small">
                        <InputLabel>Rate plans</InputLabel>
                        <Select
                          multiple
                          value={selectedPlanIds}
                          label="Rate plans"
                          onChange={(event) => handleGroupRatePlansChange(group.room_type_id, event.target.value)}
                          renderValue={() => (
                            chosenPlans.length ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {chosenPlans.map((plan) => {
                                  const key = planIdentity(plan);
                                  return (
                                    <Chip
                                      key={key}
                                      size="small"
                                      label={plan.rate_plan_code || plan.rate_plan_name}
                                      onMouseDown={(event) => event.stopPropagation()}
                                      onDelete={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleGroupRatePlansChange(
                                          group.room_type_id,
                                          selectedPlanIds.filter((id) => id !== key)
                                        );
                                      }}
                                      sx={{
                                        height: 22,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        bgcolor: alpha(THEME_COLOR, isDark ? 0.18 : 0.14),
                                        color: isDark ? THEME_COLOR : '#047857',
                                        border: `1px solid ${alpha(THEME_COLOR, 0.35)}`,
                                        '& .MuiChip-deleteIcon': {
                                          fontSize: 14,
                                          color: isDark ? THEME_COLOR : '#047857',
                                        },
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            ) : 'Select rate plans'
                          )}
                          sx={{
                            ...getGuestDrawerSelectSx(isDark),
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 0.5,
                              minHeight: 40,
                              py: 0.75,
                            },
                          }}
                          MenuProps={drawerOverlays.selectMenuProps}
                        >
                          {(group.rate_plans || []).map((plan) => (
                            <MenuItem key={planIdentity(plan)} value={planIdentity(plan)} sx={{ py: 0.5 }}>
                              <Checkbox
                                size="small"
                                checked={Boolean(plan.selected)}
                                sx={{ p: 0.5, mr: 1, color: THEME_COLOR, '&.Mui-checked': { color: THEME_COLOR } }}
                              />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                  {plan.rate_plan_name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.68rem', color: drawerTheme.subtleText }}>
                                  {plan.channel_rate_code || plan.rate_plan_code}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
          </Box>
        </Box>

        <Box
          sx={{
            p: 1.5,
            flexShrink: 0,
            zIndex: 1,
            ...getBookingDrawerFooterSx(isDark),
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleCloseDrawer}
              fullWidth
              disabled={saving}
              sx={{
                borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                color: isDark ? drawerTheme.text : '#666',
                '&:hover': {
                  borderColor: BOOKING_DRAWER_ACCENT,
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                  color: BOOKING_DRAWER_ACCENT,
                },
                '&:disabled': {
                  borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                  color: isDark ? drawerTheme.subtleText : '#999',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              fullWidth
              disabled={saving || catalogLoading}
              sx={{
                backgroundColor: BOOKING_DRAWER_ACCENT,
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: BOOKING_DRAWER_ACCENT,
                  opacity: 0.9,
                  boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                },
                '&:disabled': {
                  backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                  color: isDark ? drawerTheme.subtleText : '#666',
                },
              }}
            >
              {saving
                ? <CircularProgress size={20} color="inherit" />
                : autoMappingCount > 0
                  ? `Create ${autoMappingCount} mapping${autoMappingCount === 1 ? '' : 's'}`
                  : 'Create mappings'}
            </Button>
          </Box>
        </Box>
        </Box>
      </Drawer>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Channel Mapping</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the mapping for "{mappingToDelete?.channel_name}" - "{mappingToDelete?.room_type_name}/{mappingToDelete?.rate_plan_name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deletingId === mappingToDelete?.id}
          >
            {deletingId === mappingToDelete?.id ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={channelDeleteDialogOpen}
        onClose={() => !deletingChannelId && setChannelDeleteDialogOpen(false)}
      >
        <DialogTitle>Remove channel mappings</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove all mappings for "{channelToDelete?.channel?.name}"? This will permanently delete {channelToDelete?.mappings?.length || 0} mapping{(channelToDelete?.mappings?.length || 0) === 1 ? '' : 's'}. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChannelDeleteDialogOpen(false)} disabled={!!deletingChannelId}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteChannelConfirm}
            color="error"
            variant="contained"
            disabled={!!deletingChannelId}
          >
            {deletingChannelId ? <CircularProgress size={20} /> : 'Remove all'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChannelMappingSection;
